import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Tippy from '@tippy.js/react';
import 'tippy.js/themes/light.css';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { NoMarkErrorComponent } from './DefaultTooltipComponents';

import styles from './validatedInput.module.scss';

const ValidatedInput = React.forwardRef((props, ref) => {
  const {
    rules,
    SuccessComponent,
    ErrorComponent,
    header,
    onChange,
    error,
    clearErrors,
    clearOtherErrors,
    defaultVisible,
    showErrorAsNeutral,
    selector,
    externalValue,
    className,
    regexRule,
    children,
    initValue="",
    ...restProps
  } = props;
  const [dismissTimer, setDismissTimer] = useState(null);
  const [value, setValue] = useState(initValue || "");
  const [tooltipVisible, setTooltipVisible] = useState(
    Boolean(defaultVisible) || Boolean(error),
  );

  const resetErrors = () =>
    setTimeout(() => {
      clearErrors(props.name);
      clearOtherErrors && clearOtherErrors();
    }, 250);

  const dismissTooltip = (delay = 2000) =>
    setTimeout(() => {
      setTooltipVisible(false);
      resetErrors();
    }, delay);

  const updateInput = event => {
    if (regexRule) {
      if (regexRule.test(event.target.value) || event.target.value === '')
        setValue(event.target.value);
    } else {
      setValue(event.target.value);
    }
    if (defaultVisible) {
      clearErrors();
      if (R.allPass(rules.map(i => i.match))(event.target.value)) {
        if (!dismissTimer) {
          setDismissTimer(dismissTooltip());
        }
      } else {
        clearTimeout(dismissTimer);
        setDismissTimer(null);
        setTooltipVisible(true);
      }
    } else if (tooltipVisible) {
      dismissTooltip(0);
    }
    if (regexRule) {
      if (regexRule.test(event.target.value) || event.target.value === '')
        onChange && onChange(event);
    } else {
      onChange && onChange(event);
    }
  };
  useEffect(
    () => {
      if (error) {
        setTooltipVisible(true);
      } else if (!defaultVisible && tooltipVisible) {
        dismissTooltip(0);
      }
    },
    [error],
  );

  useEffect(
    () => {
      if (selector) {
        dismissTooltip(0);
        setValue(externalValue);
        onChange && onChange(externalValue);
      }
    },
    [externalValue, selector],
  );

  const messagesList = error
    ? [<NoMarkErrorComponent key={error}>{error}</NoMarkErrorComponent>]
    : rules.map(rule => {
        return rule.match(value) ? (
          <SuccessComponent key={rule.successMessage}>
            {rule.successMessage}
          </SuccessComponent>
        ) : (
          <ErrorComponent key={rule.errorMessage}>
            {rule.errorMessage}
          </ErrorComponent>
        );
      });
  return (
    <Tippy
      visible={tooltipVisible}
      placement="bottom"
      flip={false}
      hideOnClick={false}
      theme="light"
      zIndex={9}
      className={styles.tooltipShadowed}
      maxWidth="16.5rem"
      appendTo="parent"
      content={
        (messagesList.length && (
          <div id={styles.popover}>
            {header && <p className={styles.tooltipHeader}>{header}</p>}
            {messagesList}
          </div>
        )) || <div />
      }
    >
      {children || (
        <input
          className={classNames(
            styles.input,
            {
              [styles.inputError]:
                tooltipVisible && (!showErrorAsNeutral || error),
            },
            className,
          )}
          type="text"
          autoComplete="off"
          value={value}
          onChange={updateInput}
          ref={ref}
          {...restProps}
        />
      )}
    </Tippy>
  );
});

ValidatedInput.propTypes = {
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      match: PropTypes.func,
      successMessage: PropTypes.string,
      errorMessage: PropTypes.string,
    }),
  ),
  SuccessComponent: PropTypes.elementType.isRequired,
  ErrorComponent: PropTypes.elementType.isRequired,
  header: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  clearErrors: PropTypes.func,
  clearOtherErrors: PropTypes.func,
  defaultVisible: PropTypes.bool,
};

ValidatedInput.defaultProps = {
  rules: [],
  clearErrors: () => {},
};

export default ValidatedInput;
